import { useState, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GlassPanel, Icon } from '../components/common'
import { useShots, useShotOperations, useCharacters, useLocations, useEvents } from '../hooks/useFirestore'
import { useProject } from '../context/ProjectContext'
import { useNavigate, Link } from 'react-router-dom'
import './StoryboardView.css'

/**
 * Helper to get image URL from either string or object format
 */
const getImageUrl = (image) => {
  if (!image) return ''
  if (typeof image === 'string') return image
  return image.url || ''
}

/**
 * Format duration for display
 */
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '0.0s'
  return `${seconds.toFixed(1)}s`
}

/**
 * Format total time as mm:ss
 */
const formatTotalTime = (totalSeconds) => {
  if (!totalSeconds) return '0:00'
  const mins = Math.floor(totalSeconds / 60)
  const secs = Math.floor(totalSeconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Get layer color class
 */
const getLayerColor = (layer) => {
  const colors = {
    'A': 'cyan',
    'B': 'amber',
    'C': 'purple',
    'D': 'green',
  }
  return colors[layer] || 'cyan'
}

/**
 * Page transition variants
 */
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

/**
 * TimelineRuler - Shows cumulative duration with tick marks
 */
const TimelineRuler = ({ shots, totalDuration }) => {
  // Generate tick marks every 30 seconds
  const ticks = useMemo(() => {
    const tickInterval = 30 // seconds
    const numTicks = Math.ceil(totalDuration / tickInterval) + 1
    return Array.from({ length: numTicks }, (_, i) => i * tickInterval)
  }, [totalDuration])

  return (
    <div className="storyboard-ruler">
      <div className="storyboard-ruler__track">
        {ticks.map((seconds) => (
          <div 
            key={seconds} 
            className="storyboard-ruler__tick"
            style={{ left: totalDuration > 0 ? `${(seconds / totalDuration) * 100}%` : '0%' }}
          >
            <span className="storyboard-ruler__label">
              {formatTotalTime(seconds)}
            </span>
          </div>
        ))}
      </div>
      <div className="storyboard-ruler__total">
        Total: {formatTotalTime(totalDuration)}
      </div>
    </div>
  )
}

/**
 * QuickLinks - Shows linked entity avatars
 */
const QuickLinks = ({ shot, characters, locations, events }) => {
  const navigate = useNavigate()
  
  const linkedChars = useMemo(() => {
    if (!shot.linkedCharacters?.length) return []
    return characters.filter(c => shot.linkedCharacters.includes(c.id))
  }, [shot.linkedCharacters, characters])

  const linkedLocs = useMemo(() => {
    if (!shot.linkedLocations?.length) return []
    return locations.filter(l => shot.linkedLocations.includes(l.id))
  }, [shot.linkedLocations, locations])

  const linkedEvts = useMemo(() => {
    if (!shot.linkedEvents?.length) return []
    return events.filter(e => shot.linkedEvents.includes(e.id))
  }, [shot.linkedEvents, events])

  if (!linkedChars.length && !linkedLocs.length && !linkedEvts.length) {
    return null
  }

  return (
    <div className="storyboard-quicklinks">
      {linkedChars.slice(0, 3).map((char) => (
        <button
          key={char.id}
          className="storyboard-quicklink storyboard-quicklink--character"
          onClick={(e) => {
            e.stopPropagation()
            navigate('/characters')
          }}
          title={char.name}
        >
          {getImageUrl(char.primaryImage) ? (
            <img src={getImageUrl(char.primaryImage)} alt={char.name} />
          ) : (
            <Icon name="node-diamond" size={16} className="storyboard-quicklink__icon" />
          )}
        </button>
      ))}
      {linkedLocs.slice(0, 2).map((loc) => (
        <button
          key={loc.id}
          className="storyboard-quicklink storyboard-quicklink--location"
          onClick={(e) => {
            e.stopPropagation()
            navigate('/locations')
          }}
          title={loc.name}
        >
          {getImageUrl(loc.primaryImage) ? (
            <img src={getImageUrl(loc.primaryImage)} alt={loc.name} />
          ) : (
                <span className="storyboard-quicklink__icon"><Icon name="node-diamond" size={14} /></span>
          )}
        </button>
      ))}
      {linkedEvts.slice(0, 2).map((evt) => (
        <button
          key={evt.id}
          className="storyboard-quicklink storyboard-quicklink--event"
          onClick={(e) => {
            e.stopPropagation()
            navigate('/events')
          }}
          title={evt.title}
        >
          {getImageUrl(evt.primaryImage) ? (
            <img src={getImageUrl(evt.primaryImage)} alt={evt.title} />
          ) : (
            <Icon name="node-circle" size={16} className="storyboard-quicklink__icon" />
          )}
        </button>
      ))}
      {(linkedChars.length > 3 || linkedLocs.length > 2 || linkedEvts.length > 2) && (
        <span className="storyboard-quicklink__more">+{
          Math.max(0, linkedChars.length - 3) + 
          Math.max(0, linkedLocs.length - 2) + 
          Math.max(0, linkedEvts.length - 2)
        }</span>
      )}
    </div>
  )
}

/**
 * SortableStoryboardCard - Draggable shot card with frames
 */
const SortableStoryboardCard = ({ shot, index, characters, locations, events, isDragging, disabled = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: shot.id, disabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  }

  const navigate = useNavigate()
  const layerColor = getLayerColor(shot.layer)
  const startFrameUrl = getImageUrl(shot.primaryImage)
  
  // For end frame, check extraImages or fallback to primary
  const endFrameUrl = shot.extraImages?.length > 0 
    ? getImageUrl(shot.extraImages[0]) 
    : startFrameUrl

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`storyboard-card storyboard-card--${layerColor}`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={() => navigate('/shots')}
    >
      {/* Drag handle */}
      <div 
        className={`storyboard-card__handle ${disabled ? 'storyboard-card__handle--disabled' : ''}`} 
        {...attributes} 
        {...(disabled ? {} : listeners)}
        title={disabled ? 'Reordering disabled (view only)' : 'Drag to reorder'}
      >
        <span className="storyboard-card__grip">{disabled ? '⋯' : '⋮⋮'}</span>
      </div>

      {/* Shot info */}
      <div className="storyboard-card__info">
        <span className="storyboard-card__id">{shot.shotId || shot.title || 'Untitled'}</span>
        {shot.title && shot.shotId && (
          <span className="storyboard-card__title">{shot.title}</span>
        )}
      </div>

      {/* Frame previews */}
      <div className="storyboard-card__frames">
        <div className="storyboard-card__frame storyboard-card__frame--start">
          {startFrameUrl ? (
            <img src={startFrameUrl} alt="Start frame" />
          ) : (
            <span className="storyboard-card__frame-placeholder">START</span>
          )}
          <span className="storyboard-card__frame-label">IN</span>
        </div>
        <div className="storyboard-card__frame-arrow"><Icon name="arrow-right" size={16} /></div>
        <div className="storyboard-card__frame storyboard-card__frame--end">
          {endFrameUrl ? (
            <img src={endFrameUrl} alt="End frame" />
          ) : (
            <span className="storyboard-card__frame-placeholder">END</span>
          )}
          <span className="storyboard-card__frame-label">OUT</span>
        </div>
      </div>

      {/* Duration */}
      <div className="storyboard-card__duration">
        <span className="storyboard-card__duration-value">
          {formatDuration(shot.durationSeconds)}
        </span>
      </div>

      {/* Layer indicator */}
      <div className={`storyboard-card__layer storyboard-card__layer--${layerColor}`}>
        {shot.layer || '—'}
      </div>

      {/* Quick links */}
      <QuickLinks 
        shot={shot} 
        characters={characters} 
        locations={locations} 
        events={events} 
      />

      {/* Layer accent bar */}
      <div className={`storyboard-card__accent storyboard-card__accent--${layerColor}`} />
    </motion.div>
  )
}

/**
 * DragOverlayCard - Visual feedback during drag
 */
const DragOverlayCard = ({ shot }) => {
  if (!shot) return null

  const layerColor = getLayerColor(shot.layer)
  const startFrameUrl = getImageUrl(shot.primaryImage)
  const endFrameUrl = shot.extraImages?.length > 0 
    ? getImageUrl(shot.extraImages[0]) 
    : startFrameUrl

  return (
    <div className={`storyboard-card storyboard-card--${layerColor} storyboard-card--dragging`}>
      <div className="storyboard-card__handle">
        <span className="storyboard-card__grip">⋮⋮</span>
      </div>
      <div className="storyboard-card__info">
        <span className="storyboard-card__id">{shot.shotId || shot.title || 'Untitled'}</span>
      </div>
      <div className="storyboard-card__frames">
        <div className="storyboard-card__frame storyboard-card__frame--start">
          {startFrameUrl ? (
            <img src={startFrameUrl} alt="Start frame" />
          ) : (
            <span className="storyboard-card__frame-placeholder">START</span>
          )}
          <span className="storyboard-card__frame-label">IN</span>
        </div>
        <div className="storyboard-card__frame-arrow"><Icon name="arrow-right" size={16} /></div>
        <div className="storyboard-card__frame storyboard-card__frame--end">
          {endFrameUrl ? (
            <img src={endFrameUrl} alt="End frame" />
          ) : (
            <span className="storyboard-card__frame-placeholder">END</span>
          )}
          <span className="storyboard-card__frame-label">OUT</span>
        </div>
      </div>
      <div className="storyboard-card__duration">
        <span className="storyboard-card__duration-value">
          {formatDuration(shot.durationSeconds)}
        </span>
      </div>
      <div className={`storyboard-card__layer storyboard-card__layer--${layerColor}`}>
        {shot.layer || '—'}
      </div>
      <div className={`storyboard-card__accent storyboard-card__accent--${layerColor}`} />
    </div>
  )
}

/**
 * StoryboardView - Main storyboard view with drag-and-drop
 */
export default function StoryboardView() {
  const [activeId, setActiveId] = useState(null)

  // Project context for scoped data access and permissions
  const { projectId, can } = useProject()
  const canEdit = can('edit')
  
  // Firestore data (project-scoped)
  const { data: rawShots, loading: shotsLoading, error: shotsError } = useShots(projectId, {
    orderByField: 'createdAt',
    orderDirection: 'asc',
  })
  const { data: characters } = useCharacters(projectId)
  const { data: locations } = useLocations(projectId)
  const { data: events } = useEvents(projectId)
  const shotOps = useShotOperations(projectId)

  // Sort shots by order field if present, otherwise by createdAt
  const shots = useMemo(() => {
    return [...rawShots].sort((a, b) => {
      const orderA = a.order ?? Infinity
      const orderB = b.order ?? Infinity
      if (orderA !== orderB) return orderA - orderB
      // Fallback to createdAt
      const timeA = a.createdAt?.toDate?.() || new Date(0)
      const timeB = b.createdAt?.toDate?.() || new Date(0)
      return timeA - timeB
    })
  }, [rawShots])

  // Calculate total duration
  const totalDuration = useMemo(() => {
    return shots.reduce((sum, shot) => sum + (shot.durationSeconds || 0), 0)
  }, [shots])

  // Get active shot for drag overlay
  const activeShot = useMemo(() => {
    return shots.find(s => s.id === activeId)
  }, [activeId, shots])

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id)
  }

  // Handle drag end - reorder and persist (only if user can edit)
  const handleDragEnd = async (event) => {
    const { active, over } = event
    setActiveId(null)

    // Don't allow reordering if user doesn't have edit permission
    if (!canEdit) return

    if (!over || active.id === over.id) return

    const oldIndex = shots.findIndex(s => s.id === active.id)
    const newIndex = shots.findIndex(s => s.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    // Reorder locally
    const reordered = arrayMove(shots, oldIndex, newIndex)

    // Persist new order to Firestore
    try {
      const updatePromises = reordered.map((shot, index) => {
        if (shot.order !== index) {
          return shotOps.update(shot.id, { order: index })
        }
        return Promise.resolve()
      })
      await Promise.all(updatePromises)
    } catch (err) {
      console.error('Failed to persist order:', err)
    }
  }

  const loading = shotsLoading
  const error = shotsError

  return (
    <motion.div 
      className="storyboard-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="storyboard-view__header">
        <div className="storyboard-view__title-group">
          <span className="storyboard-view__icon"><Icon name="storyboard" size={28} /></span>
          <h1 className="storyboard-view__title">Storyboard</h1>
        </div>
        <p className="storyboard-view__subtitle">
          {loading ? 'Loading...' : `${shots.length} shot${shots.length !== 1 ? 's' : ''} • ${formatTotalTime(totalDuration)} total`}
        </p>
      </header>

      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="storyboard-view__error">
          <h3>Error loading shots</h3>
          <p>{error.message}</p>
        </GlassPanel>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="storyboard-view__loading">
          <div className="storyboard-view__spinner" />
          <p>Loading storyboard...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && shots.length === 0 && (
        <GlassPanel glow="cyan" padding="xl" className="storyboard-view__empty">
          <div className="storyboard-view__empty-icon"><Icon name="storyboard" size={48} /></div>
          <h2>No shots in storyboard</h2>
          <p>Add shots from the Shots view to start building your storyboard.</p>
          <Link to="shots" className="storyboard-view__empty-link">
            Go to Shots <Icon name="arrow-right" size={14} />
          </Link>
        </GlassPanel>
      )}

      {/* Storyboard Content */}
      {!loading && !error && shots.length > 0 && (
        <>
          {/* Timeline Ruler */}
          <TimelineRuler shots={shots} totalDuration={totalDuration} />

          {/* Sortable Shot List */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={shots.map(s => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="storyboard-view__list">
                {shots.map((shot, index) => (
                  <SortableStoryboardCard
                    key={shot.id}
                    shot={shot}
                    index={index}
                    characters={characters}
                    locations={locations}
                    events={events}
                    isDragging={activeId === shot.id}
                    disabled={!canEdit}
                  />
                ))}
              </div>
            </SortableContext>

            <DragOverlay>
              <DragOverlayCard shot={activeShot} />
            </DragOverlay>
          </DndContext>
        </>
      )}

      {/* Legend */}
      {!loading && !error && shots.length > 0 && (
        <div className="storyboard-view__legend">
          <span className="storyboard-view__legend-title">Layers:</span>
          <div className="storyboard-view__legend-item storyboard-view__legend-item--cyan">A</div>
          <div className="storyboard-view__legend-item storyboard-view__legend-item--amber">B</div>
          <div className="storyboard-view__legend-item storyboard-view__legend-item--purple">C</div>
          <div className="storyboard-view__legend-item storyboard-view__legend-item--green">D</div>
        </div>
      )}
    </motion.div>
  )
}

